import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Play, X, CheckCircle2, MessageSquare, Target, Upload, TrendingUp, ChevronDown, ShieldCheck } from 'lucide-react';
import { featuredAPI } from '../lib/api';
import HLSPlayer from '../components/HLSPlayer';
import LandingVideoPopup from '../components/LandingVideoPopup';

const FEATURES = [
  { slot: "personalized_drills", icon: Target, title: "FOLLOW YOUR DAILY PLAN", desc: "Get structured shooting, stickhandling, and workout progressions designed to build your game over time." },
  { slot: "video_instruction", icon: Upload, title: "TRAIN & RECORD", desc: "Upload your clips directly in the app." },
  { slot: "coach_feedback", icon: MessageSquare, title: "GET COACH FEEDBACK", desc: "Message our coaches and get real corrections\u2014no guessing on YouTube or social media." },
  { slot: "progress_tracking", icon: TrendingUp, title: "IMPROVE FASTER", desc: "Fix mistakes immediately and build real habits." },
];

const APP_PREVIEWS = [
  { src: "/preview/06-ad-poster.jpg", type: "poster", title: "The System", caption: "Train smart. Improve fast. Save thousands.", dwellMs: 6000 },
  { src: "/preview/01-weekly-plan.jpg", type: "screenshot", title: "Your Weekly Plan", caption: "Daily shooting & stickhandling sessions, mapped out.", dwellMs: 4000 },
  { src: "/preview/02.jpg", type: "screenshot", title: "Weekly Challenges", caption: "Submit a clip. Earn rewards. Stay sharp.", dwellMs: 4000 },
  { src: "/preview/03.jpg", type: "screenshot", title: "Live Leaderboards", caption: "Filter by age, gender, and position.", dwellMs: 4000 },
  { src: "/preview/04.jpg", type: "screenshot", title: "Streaks That Stick", caption: "Train consistently. Climb the rankings.", dwellMs: 4000 },
  { src: "/preview/05.jpg", type: "screenshot", title: "Full Drill Library", caption: "Hundreds of progressive hockey drills.", dwellMs: 4000 },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [featuredVideos, setFeaturedVideos] = useState({});
  const [activeVideo, setActiveVideo] = useState(null);
  const [expandedCoach, setExpandedCoach] = useState(null);
  const previewScrollRef = useRef(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const previewIndexRef = useRef(0);
  const [previewProgress, setPreviewProgress] = useState(0); // 0-100, fills over the active slide's dwell window
  const previewPausedUntilRef = useRef(0);
  const previewElapsedRef = useRef(0); // ms accumulated toward next auto-advance

  const DEFAULT_PREVIEW_AUTOPLAY_MS = 4000;
  const getActiveDwellMs = () => {
    const idx = previewIndexRef.current;
    return APP_PREVIEWS[idx]?.dwellMs || DEFAULT_PREVIEW_AUTOPLAY_MS;
  };

  const scrollPreview = (dir) => {
    const el = previewScrollRef.current;
    if (!el) return;
    const card = el.querySelector('[data-preview-card]');
    const step = card ? card.offsetWidth + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const onPreviewScroll = () => {
    const el = previewScrollRef.current;
    if (!el) return;
    const card = el.querySelector('[data-preview-card]');
    const step = card ? card.offsetWidth + 16 : 1;
    const newIdx = Math.round(el.scrollLeft / step);
    setPreviewIndex(prev => {
      if (prev !== newIdx) {
        // Reset progress so the bar restarts on the new active slide
        previewElapsedRef.current = 0;
        setPreviewProgress(0);
        previewIndexRef.current = newIdx;
      }
      return newIdx;
    });
  };

  const pausePreviewAutoplay = () => {
    // Pause auto-advance for 8s after any user interaction; reset progress bar
    previewPausedUntilRef.current = Date.now() + 8000;
    previewElapsedRef.current = 0;
    setPreviewProgress(0);
  };

  // Auto-advance + progress-bar tick (every 100ms), with per-slide dwell duration
  useEffect(() => {
    const tickMs = 100;
    const interval = setInterval(() => {
      const el = previewScrollRef.current;
      if (!el) return;
      if (Date.now() < previewPausedUntilRef.current) return;
      const dwellMs = getActiveDwellMs();
      previewElapsedRef.current += tickMs;
      const ratio = Math.min(1, previewElapsedRef.current / dwellMs);
      setPreviewProgress(ratio * 100);
      if (previewElapsedRef.current >= dwellMs) {
        const card = el.querySelector('[data-preview-card]');
        if (!card) return;
        const step = card.offsetWidth + 16;
        const maxScroll = el.scrollWidth - el.clientWidth - 4;
        const atEnd = el.scrollLeft >= maxScroll;
        if (atEnd) {
          el.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          el.scrollBy({ left: step, behavior: 'smooth' });
        }
        previewElapsedRef.current = 0;
        setPreviewProgress(0);
      }
    }, tickMs);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    featuredAPI.getAll()
      .then(res => setFeaturedVideos(res.data || {}))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#060913] overflow-x-hidden">

      <LandingVideoPopup />

      {/* HERO */}
      <section className="relative px-6 safe-top pt-10 pb-2">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1765302966993-0633da24b61d?w=1200)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060913]/60 via-[#060913]/80 to-[#060913]" />

        <div className="relative z-10 max-w-lg mx-auto w-full">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 flex justify-center"
          >
            <img src="/logo-main.png" alt="Elev802 Connected" className="h-56 sm:h-64 w-auto drop-shadow-2xl rounded-2xl" data-testid="logo" />
          </motion.div>

          {/* Headline + Subheadline (centered) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-center"
          >
            <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black leading-[1] tracking-tight mb-3 text-white" data-testid="hero-headline">
              Train With Purpose.<br />
              <span className="text-[#2563EB]">Start Your Program.</span>
            </h1>
            <p className="text-white text-base sm:text-lg font-semibold leading-snug max-w-md mx-auto" data-testid="hero-subheadline">
              Most players train. <span className="text-[#2563EB]">Few actually improve.</span>
            </p>
            <p className="text-[#94A3B8] text-xs sm:text-sm leading-relaxed max-w-md mx-auto mt-2" data-testid="hero-subheadline-2">
              Elev802 Connected gives players a clear path to consistent development.
            </p>
          </motion.div>
        </div>
      </section>

      {/* HERO CTA — Sign-up buttons (right above What's Inside) */}
      <section className="px-6 pt-2 pb-6 bg-[#060913]">
        <div className="max-w-lg mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            {/* Founding Member Pricing callout */}
            <div
              className="bg-[#FF9100]/10 border border-[#FF9100]/40 rounded-lg px-4 py-3 text-center"
              data-testid="founding-member-callout"
            >
              <p className="text-[#FF9100] text-[10px] font-black uppercase tracking-widest">
                Founding Member Pricing
              </p>
              <p className="text-white text-xs font-semibold mt-0.5">
                First <span className="text-[#FF9100]">500 players</span> lock in $14.99/month
              </p>
            </div>

            <button
              onClick={() => navigate('/register')}
              className="w-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF] text-[#060913] font-black uppercase tracking-wide rounded-lg px-8 py-4 transition-all hover:opacity-90 flex items-center justify-center gap-2 text-base"
              data-testid="start-training-btn"
            >
              Start Week 1
              <ChevronRight size={20} />
            </button>

            <div className="text-center mt-3 space-y-1">
              <p className="text-white text-sm font-semibold">
                Built by "Swaggy P" &mdash; Pete Lenes and the team at Elev802!
              </p>
              <p className="text-[#00E5FF] text-xs font-medium">
                5 days free, then $14.99/month &mdash; cancel anytime
              </p>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="w-full bg-transparent border-2 border-[#1C2B54] hover:border-[#2563EB] text-white font-bold uppercase tracking-wide rounded-lg px-8 py-4 transition-all text-sm mt-4"
              data-testid="login-btn"
            >
              Already a Member? Sign In
            </button>
          </motion.div>
        </div>
      </section>

      {/* WHAT'S INSIDE — App Preview Carousel (right under headline) */}
      <section className="px-0 pt-4 pb-8 bg-[#060913]">
        <div className="max-w-lg mx-auto px-6 text-center mb-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-['Barlow_Condensed'] text-2xl font-black tracking-tight text-white mb-1"
          >
            What's <span className="text-[#2563EB]">Inside</span>
          </motion.h2>
          <p className="text-[#94A3B8] text-xs leading-relaxed max-w-sm mx-auto">
            A look at the system you'll train with every day.
          </p>
        </div>

        <div className="relative">
          {/* Edge fade overlays */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-6 sm:w-12 bg-gradient-to-r from-[#060913] to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-6 sm:w-12 bg-gradient-to-l from-[#060913] to-transparent z-10" />

          <div
            ref={previewScrollRef}
            onScroll={onPreviewScroll}
            onTouchStart={pausePreviewAutoplay}
            onMouseDown={pausePreviewAutoplay}
            onWheel={pausePreviewAutoplay}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-6 px-6 sm:px-12 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            data-testid="app-preview-carousel"
          >
            {APP_PREVIEWS.map((p, i) => (
              <motion.div
                key={p.src}
                data-preview-card
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-10%' }}
                transition={{ delay: i * 0.08 }}
                className="snap-center flex-shrink-0 w-[260px] sm:w-[280px]"
                data-testid={`app-preview-${i}`}
              >
                {p.type === 'poster' ? (
                  // Marketing poster — flat card, no phone frame
                  <div className="relative mx-auto w-[240px] sm:w-[260px]">
                    <div className="rounded-2xl overflow-hidden bg-black shadow-2xl shadow-[#2563EB]/20 border border-[#1C2B54] ring-1 ring-[#2563EB]/30">
                      <img
                        src={p.src}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-auto select-none"
                        draggable={false}
                      />
                    </div>
                    {/* Spacer so heights roughly align with phone slides */}
                    <div className="mx-auto mt-2 h-2 w-32 rounded-full bg-[#2563EB]/20 blur-md" />
                    {/* Progress bar */}
                    <div className="mx-auto mt-3 h-[3px] w-32 rounded-full bg-[#1C2B54]/70 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF] rounded-full"
                        style={{
                          width: i === previewIndex ? `${previewProgress}%` : '0%',
                          transition: i === previewIndex && previewProgress > 0 ? 'width 100ms linear' : 'none',
                        }}
                        data-testid={`preview-progress-${i}`}
                      />
                    </div>
                  </div>
                ) : (
                  // App screenshot — iPhone-style mockup
                  <div className="relative mx-auto w-[240px] sm:w-[260px]">
                    <div className="relative bg-[#0F172A] rounded-[2.5rem] p-2 shadow-2xl shadow-[#2563EB]/10 border border-[#1C2B54]">
                      {/* Notch */}
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#0F172A] rounded-b-2xl z-20 flex items-center justify-center gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#1C2B54]" />
                        <div className="w-10 h-1 rounded-full bg-[#1C2B54]" />
                      </div>
                      <div className="rounded-[2rem] overflow-hidden bg-black aspect-[9/19.5]">
                        <img
                          src={p.src}
                          alt={p.title}
                          loading="lazy"
                          className="w-full h-full object-cover object-top select-none"
                          draggable={false}
                        />
                      </div>
                    </div>
                    {/* Subtle floor reflection */}
                    <div className="mx-auto mt-2 h-2 w-32 rounded-full bg-[#2563EB]/20 blur-md" />
                    {/* Progress bar — only on active slide */}
                    <div className="mx-auto mt-3 h-[3px] w-32 rounded-full bg-[#1C2B54]/70 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF] rounded-full"
                        style={{
                          width: i === previewIndex ? `${previewProgress}%` : '0%',
                          transition: i === previewIndex && previewProgress > 0 ? 'width 100ms linear' : 'none',
                        }}
                        data-testid={`preview-progress-${i}`}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-4 text-center px-2">
                  <p className="font-['Barlow_Condensed'] text-base font-bold text-white tracking-tight uppercase">
                    {p.title}
                  </p>
                  <p className="text-xs text-[#94A3B8] mt-1 leading-snug">
                    {p.caption}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Arrow controls (desktop only) */}
          <button
            type="button"
            onClick={() => { pausePreviewAutoplay(); scrollPreview(-1); }}
            aria-label="Previous screenshot"
            className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-[#0B132B]/90 border border-[#1C2B54] hover:border-[#2563EB] items-center justify-center text-white transition-colors"
            data-testid="preview-prev-btn"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => { pausePreviewAutoplay(); scrollPreview(1); }}
            aria-label="Next screenshot"
            className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-[#0B132B]/90 border border-[#1C2B54] hover:border-[#2563EB] items-center justify-center text-white transition-colors"
            data-testid="preview-next-btn"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Counter + Dots */}
        <div className="flex items-center justify-center gap-3 mt-2">
          <span className="text-[10px] font-bold text-[#94A3B8] tabular-nums uppercase tracking-widest" data-testid="preview-counter">
            {Math.min(previewIndex + 1, APP_PREVIEWS.length)} <span className="text-[#475569]">/</span> {APP_PREVIEWS.length}
          </span>
          <div className="flex items-center gap-2">
            {APP_PREVIEWS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === previewIndex ? 'w-6 bg-[#2563EB]' : 'w-1.5 bg-[#1C2B54]'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="max-w-lg mx-auto px-6 mt-6 text-center">
          <p className="text-[10px] text-[#64748B] uppercase tracking-widest">
            Swipe to explore &rarr;
          </p>
        </div>
      </section>

      {/* TRUST STRIP — thin social-proof bar below carousel */}
      <section className="px-6 py-4 bg-[#060913] border-y border-[#1C2B54]/50" data-testid="trust-strip">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-lg mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6"
        >
          <div className="flex items-center gap-2" data-testid="trust-built-for-hockey">
            <ShieldCheck size={16} className="text-[#00E5FF] flex-shrink-0" strokeWidth={2.5} />
            <span className="text-[10px] sm:text-xs font-black text-white uppercase tracking-widest whitespace-nowrap">
              Built For Hockey
            </span>
          </div>
          <div className="hidden sm:block h-4 w-px bg-[#1C2B54]" />
          <div className="flex items-center gap-2" data-testid="trust-structured-dev">
            <TrendingUp size={16} className="text-[#FF9100] flex-shrink-0" strokeWidth={2.5} />
            <span className="text-[10px] sm:text-xs font-black text-white uppercase tracking-widest whitespace-nowrap">
              Structured Development. <span className="text-[#FF9100]">Real Progression.</span>
            </span>
          </div>
        </motion.div>
      </section>

      {/* SECTION 1 — WHAT THIS ACTUALLY IS */}
      <section className="px-6 py-16">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="font-['Barlow_Condensed'] text-3xl font-black tracking-tight text-white mb-3">
              This Isn't Another Training App
            </h2>
            <p className="text-[#CBD5E1] text-sm leading-relaxed max-w-sm mx-auto">
              Most training apps overwhelm players with random drills.{' '}
              <span className="text-white font-semibold">Elev802 Connected delivers a structured progression players can actually follow.</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-[#0B132B] border border-[#1C2B54] rounded-2xl p-6 space-y-4"
          >
            <p className="text-white font-semibold text-sm mb-2">You get:</p>
            <div className="space-y-3">
              {[
                "A daily progression plan for shooting, stickhandling, and workouts",
                "Step-by-step development, not random tips",
                "Direct access to real coaches inside the app",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-[#10B981] flex-shrink-0 mt-0.5" />
                  <p className="text-[#CBD5E1] text-sm">{item}</p>
                </div>
              ))}
            </div>
            <p className="text-[#00E5FF] text-sm font-bold mt-4 pt-4 border-t border-[#1C2B54]">
              It's like having a coach in your pocket.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — HOW IT WORKS (Feature boxes with videos) */}
      <section className="px-6 py-16 bg-[#0A0E1A]">
        <div className="max-w-lg mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-['Barlow_Condensed'] text-3xl font-black tracking-tight mb-8 text-center text-white"
          >
            How You <span className="text-[#2563EB]">Improve</span>
          </motion.h2>
          <div className="space-y-4">
            {FEATURES.map(({ slot, icon: Icon, title, desc }, i) => {
              const featured = featuredVideos[slot];
              const hasVideo = featured && featured.video_filename;

              return (
                <motion.div
                  key={slot}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => hasVideo && setActiveVideo(featured)}
                  className={`bg-[#0B132B] border border-[#1C2B54] rounded-xl overflow-hidden transition-colors ${
                    hasVideo ? 'hover:border-[#2563EB] cursor-pointer' : 'hover:border-[#2563EB]/50'
                  }`}
                  data-testid={`feature-${slot}`}
                >
                  {hasVideo && (
                    <div className="relative bg-[#060913] aspect-video flex items-center justify-center group">
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] to-transparent z-10" />
                      <div className="w-14 h-14 rounded-full bg-[#2563EB]/90 flex items-center justify-center z-20 group-hover:bg-[#2563EB] group-hover:scale-110 transition-all shadow-xl shadow-[#2563EB]/20">
                        <Play size={24} className="text-white ml-1" fill="white" />
                      </div>
                      <span className="absolute bottom-3 left-3 text-[9px] text-[#00E5FF] font-bold uppercase tracking-widest z-20">
                        Tap to preview
                      </span>
                    </div>
                  )}
                  <div className="p-5 flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-lg bg-[#2563EB]/15 flex items-center justify-center flex-shrink-0">
                      <Icon size={20} className="text-[#2563EB]" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-[#FF9100] uppercase tracking-widest">{i + 1}.</span>
                        <h3 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight text-white">{title}</h3>
                      </div>
                      <p className="text-sm text-[#CBD5E1] mt-1">{desc}</p>
                    </div>
                    {hasVideo && (
                      <div className="flex-shrink-0 self-center">
                        <Play size={16} className="text-[#2563EB]" />
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — THE VALUE */}
      <section className="px-6 py-16">
        <div className="max-w-lg mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-['Barlow_Condensed'] text-3xl font-black tracking-tight mb-8 text-center text-white"
          >
            What You Get <span className="text-[#2563EB]">Every Month</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#0B132B] border border-[#1C2B54] rounded-2xl p-6 space-y-3 mb-8"
          >
            {[
              "Daily shooting & stickhandling progressions",
              "Weekly workout programming",
              "Nutrition + mental performance guidance",
              "Direct messaging with Elev802 coaches",
              "Ongoing development plan (not one-off content)",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle2 size={16} className="text-[#10B981] flex-shrink-0" />
                <p className="text-white text-sm font-medium">{item}</p>
              </div>
            ))}
          </motion.div>

          {/* Anchor comparison */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onClick={() => navigate('/register')}
            className="bg-gradient-to-r from-[#FF9100]/10 to-[#2563EB]/10 border border-[#FF9100]/30 rounded-2xl p-6 text-center cursor-pointer hover:border-[#FF9100]/60 transition-colors"
            data-testid="value-comparison"
          >
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between px-4">
                <span className="text-[#CBD5E1] text-sm">One private lesson:</span>
                <span className="text-white font-bold text-lg">$100+</span>
              </div>
              <div className="h-px bg-[#1C2B54]" />
              <div className="flex items-center justify-between px-4">
                <span className="text-[#00E5FF] text-sm font-semibold">Elev802 Connected:</span>
                <div>
                  <span className="text-white font-black text-lg">$14.99</span>
                  <span className="text-[#94A3B8] text-sm font-medium">/month</span>
                </div>
              </div>
            </div>
            <p className="text-[#FF9100] text-xs font-bold uppercase tracking-wider">
              First 1,000 users earn an exclusive Founders Badge
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 4 — WHO THIS IS FOR */}
      <section className="px-6 py-16 bg-[#0A0E1A]">
        <div className="max-w-lg mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-['Barlow_Condensed'] text-3xl font-black tracking-tight mb-8 text-center text-white"
          >
            Built for Players Who Are<br /><span className="text-[#2563EB]">Serious About Getting Better</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            {[
              "Players who want to improve faster than their competition",
              "Athletes who don't have constant access to coaches",
              "Parents who want real development\u2014not wasted ice time",
            ].map((item, i) => (
              <div key={i} className="bg-[#0B132B] border border-[#1C2B54] rounded-xl p-4 flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-[#2563EB]/15 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={16} className="text-[#2563EB]" />
                </div>
                <p className="text-white text-sm font-medium">{item}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* COACHES SECTION */}
      <section className="px-6 py-16">
        <div className="max-w-lg mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-['Barlow_Condensed'] text-3xl font-black tracking-tight mb-8 text-center text-white"
          >
            Meet <span className="text-[#2563EB]">Your Coaches</span>
          </motion.h2>

          <div className="space-y-4">
            {[
              { name: "Jon Hutcheon", credential: "Former NHL Skills Coach | Player Development Coach", bio: "Jon Hutcheon built Elev802 Connected to solve a problem he saw firsthand\u2014players aren't lacking effort, they're lacking structure, consistency, and access to real coaching.\n\nWith a background in hockey and a deep understanding of how players actually improve, Jon focuses on creating systems that make development simple, repeatable, and effective. His approach is built around daily habits, clear progression, and giving players a plan they can follow with confidence.\n\nInside Elev802 Connected, Jon leads the vision\u2014bringing together coaching, structure, and technology to deliver a complete development system." },
              { name: "Dan Correale", credential: "Former NCAA D1 + Pro Player | Player Development Coach", bio: "Dan Correale brings a structured, development-first approach to helping players improve consistently. With a strong focus on progression, discipline, and fundamentals, he's worked with athletes to build habits that translate directly to game performance.\n\nHis coaching emphasizes doing the right things daily\u2014turning small improvements into long-term results.\n\nInside Elev802 Connected, Dan helps build the training structure that keeps players progressing week after week." },
              { name: 'Pete "Swaggy P" Lenes', credential: "Former NCAA D1 + Pro Player | Elite Skills Coach", bio: 'Pete "Swaggy P" Lenes is known for his creative approach to skill development and helping players unlock confidence with the puck. With years of experience training high-level athletes, he focuses on stickhandling, creativity, and game-real skill application.\n\nHis coaching style blends innovation with proven fundamentals\u2014giving players tools they can actually use in games, not just drills.\n\nInside Elev802 Connected, Pete helps design skill progressions that build confidence and creativity over time.' },
            ].map((coach, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#0B132B] border border-[#1C2B54] rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedCoach(expandedCoach === i ? null : i)}
                  className="w-full p-4 flex items-center gap-4 text-left"
                  data-testid={`coach-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#2563EB]/10 border border-[#2563EB]/20 flex items-center justify-center flex-shrink-0">
                    <span className="font-['Barlow_Condensed'] text-lg font-black text-[#2563EB]">
                      {coach.name.split(' ').filter(w => !w.startsWith('"')).map(w => w[0]).join('')}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Barlow_Condensed'] text-base font-bold text-white">{coach.name}</h3>
                    <p className="text-[10px] text-[#CBD5E1] leading-relaxed">{coach.credential}</p>
                  </div>
                  <ChevronDown size={16} className={`text-[#64748B] transition-transform ${expandedCoach === i ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {expandedCoach === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-0 border-t border-[#1C2B54]">
                        <p className="text-sm text-[#CBD5E1] leading-relaxed whitespace-pre-line mt-3">{coach.bio}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — URGENCY + CTA */}
      <section className="px-6 py-16">
        <div className="max-w-lg mx-auto text-center space-y-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Barlow_Condensed'] text-3xl font-black tracking-tight text-white mb-3">
              Start Training <span className="text-[#2563EB]">Today</span>
            </h2>
            <p className="text-[#CBD5E1] text-sm leading-relaxed max-w-sm mx-auto mb-6">
              Don't wait for your next practice to get better. Start improving your game right now with daily coaching and feedback.
            </p>
          </motion.div>

          <button
            onClick={() => navigate('/register')}
            className="w-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF] text-[#060913] font-black uppercase tracking-wide rounded-lg px-8 py-4 transition-all hover:opacity-90 flex items-center justify-center gap-2 text-base"
            data-testid="urgency-cta-btn"
          >
            Join Elev802 Connected
            <ChevronRight size={20} />
          </button>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section className="px-6 py-16 bg-[#0A0E1A]">
        <div className="max-w-lg mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Barlow_Condensed'] text-3xl font-black tracking-tight text-white mb-2">
              Simple. Powerful. Effective.
            </h2>
            <div className="my-6">
              <span className="font-['Barlow_Condensed'] text-6xl font-black text-white">$14.99</span>
              <span className="text-[#94A3B8] text-lg font-medium">/month</span>
              <p className="text-[#94A3B8] text-sm mt-2">5 days free, then $14.99/month — cancel anytime</p>
            </div>

            <div className="bg-[#0B132B] border border-[#1C2B54] rounded-2xl p-6 text-left space-y-3 mb-6">
              {[
                "Full access to all training programs",
                "Daily progression system",
                "Direct coach messaging",
                "Cancel anytime",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 size={16} className="text-[#10B981] flex-shrink-0" />
                  <p className="text-white text-sm">{item}</p>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/register')}
              className="w-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF] text-[#060913] font-black uppercase tracking-wide rounded-lg px-8 py-4 transition-all hover:opacity-90 flex items-center justify-center gap-2 text-base"
              data-testid="pricing-cta-btn"
            >
              Start Week 1 &mdash; then $14.99/mo
              <ChevronRight size={20} />
            </button>
            <p className="text-[#FF9100] text-xs font-bold mt-3">
              First 1,000 users earn an exclusive Founders Badge
            </p>
          </motion.div>
        </div>
      </section>

      {/* FINAL CLOSE */}
      <section className="px-6 py-16">
        <div className="max-w-lg mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <p className="text-white text-base font-semibold leading-relaxed">
              If you're serious about your game, you need more than occasional coaching.
            </p>
            <p className="text-[#CBD5E1] text-sm leading-relaxed">
              You need consistent feedback and a real plan.
            </p>
            <button
              onClick={() => navigate('/register')}
              className="mt-4 bg-transparent border-2 border-[#2563EB] hover:bg-[#2563EB]/10 text-white font-bold uppercase tracking-wide rounded-lg px-8 py-4 transition-all text-sm inline-flex items-center gap-2"
              data-testid="final-cta-btn"
            >
              Join Elev802 Connected
              <ChevronRight size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#060913]/95 flex items-center justify-center px-4"
            onClick={() => setActiveVideo(null)}
            data-testid="video-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-lg bg-[#0B132B] rounded-2xl overflow-hidden border border-[#1C2B54] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative aspect-video bg-black">
                <HLSPlayer videoFilename={activeVideo.video_filename} />
                <button
                  onClick={() => setActiveVideo(null)}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#060913]/80 flex items-center justify-center hover:bg-[#060913] transition-colors z-10"
                  data-testid="close-video-modal"
                >
                  <X size={16} className="text-white" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
